<template>
  <div>
    <button
      id="satList-button"
      ref="satListButton"
      class="cesium-button cesium-toolbar-button"
      title="Geometries list"
    >
      <v-icon
        id="satList-icon"
        name="satellite"
        scale="1"
        color="white"
        title="Geometries list"
      ></v-icon>
    </button>
    <div id="toolbar-satellites-list" ref="satListDropdown">
      <div
        v-for="(constellation, i) in constellationsUIElements"
        :key="i"
        class="satellite-row"
      >
        <div class="satellite-name-col" :title="constellation.name">
          <div
            class="dot-satellite ml-2 mr-2"
            v-bind:style="{ 'background-color': constellation.color }"
          ></div>
          <span class="legendText mr-2">{{ constellation.name }} </span>
        </div>
        <div
          class="satellite-icon-col clickable"
          @click.prevent="updateSatelliteDisplay(i)"
        >
          <v-icon
            :ref="`satListEye${i}`"
            :name="constellation.eyeIcon"
            scale="1.2"
            color="white"
            title="display satellite"
          >
          </v-icon>
        </div>
        <div
          class="satellite-icon-col clickable"
          @click.prevent="updateGroundStations(i)"
          title="Show ground stations visibility for this geometry"
        >
          <v-icon
            :ref="`groundStationIcon${i}`"
            name="satellite-dish"
            scale="1.2"
            :color="constellation.groundStationIconColor"
            title="display ground stations visibility for this geometry"
          >
          </v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CesiumInitialization } from './CesiumInitialization'
import 'vue-awesome/icons'
import Icon from 'vue-awesome/components/Icon'

export default {
  name: 'cesium-satellites-list',
  mixins: [CesiumInitialization],
  props: [
    'constellations',
    'tracesPrimitiveCollection',
    'satellitesDS',
    'isAnalyticalDashboard'
  ],

  components: {
    'v-icon': Icon
  },

  data() {
    return {
      constellationsUIElements: [],
      selectedTracesToShow: []
    }
  },

  computed: {
    cesiumViewer() {
      return this.$parent.viewer
    },
    visibilityData() {
      let visibilityData = Object.values(
        this.$parent.currentResultsLog.results.constellationsVisibilityData
      )
      visibilityData.sort((a, b) =>
        a.constellation.name < b.constellation.name ? -1 : 1
      )
      return visibilityData
    }
  },

  mounted() {
    this.updateConstellationsUIElements()
  },

  methods: {
    insertSatellitesListInToolbar(container) {
      this.insertElementInToolbar(this.$refs.satListDropdown, container)
      this.insertElementInToolbar(this.$refs.satListButton, container)
    },

    updateConstellationsUIElements() {
      let paramsList = []
      this.constellations.forEach((constellation, index) => {
        paramsList.push({
          name: constellation.name,
          color: this.$parent.colorsArray[index].toCssColorString(),
          eyeIcon: 'eye',
          groundStationIconColor: index === 0 ? 'green' : 'white'
        })
      })
      this.constellationsUIElements = paramsList
    },

    updateSatelliteDisplay(index) {
      const iconName = this.constellationsUIElements[index].eyeIcon
      if (iconName === 'eye') {
        this.constellationsUIElements[index].eyeIcon = 'eye-slash'
      } else {
        this.constellationsUIElements[index].eyeIcon = 'eye'
      }

      let planesPerGeometry = this.$parent.geometries.map(
        (geometry) => geometry.numberOfPlanes
      )
      let nbOfSatsPerPlane = this.getSatsPerPlaneArray(planesPerGeometry)
      let [indexOfFirstPrimitiveInGeometry, indexOfLastPrimitivesInGeometry] =
        this.getPlanesIndexes(index, planesPerGeometry)
      let [indexOfFirstSat, indexOfLastSat] = this.getSatelliteIndexes(
        nbOfSatsPerPlane,
        indexOfFirstPrimitiveInGeometry,
        indexOfLastPrimitivesInGeometry
      )
      this.togglePlanes(
        this.tracesPrimitiveCollection,
        indexOfFirstPrimitiveInGeometry,
        indexOfLastPrimitivesInGeometry
      )
      if (this.satellitesDS) {
        this.toggleSatellites(
          this.satellitesDS.entities.values,
          indexOfFirstSat,
          indexOfLastSat
        )
      }
    },

    getSatsPerPlaneArray(planesPerGeometry) {
      let nbOfSatsPerPlane = []
      this.$parent.geometries.forEach((geometry, geometryIndex) => {
        for (
          let planeIndex = 0;
          planeIndex < planesPerGeometry[geometryIndex];
          planeIndex++
        ) {
          nbOfSatsPerPlane.push(geometry.numberOfSpacecraftsPerPlane)
        }
      })
      return nbOfSatsPerPlane
    },

    getPlanesIndexes(geometryIndex, planesPerGeometry) {
      let indexOfFirstPrimitiveInGeometry = 0
      for (let i = 0; i < geometryIndex; i++) {
        indexOfFirstPrimitiveInGeometry += planesPerGeometry[i]
      }
      let indexOfLastPrimitivesInGeometry =
        indexOfFirstPrimitiveInGeometry + planesPerGeometry[geometryIndex]
      return [indexOfFirstPrimitiveInGeometry, indexOfLastPrimitivesInGeometry]
    },

    getSatelliteIndexes(
      nbOfSatsPerPlane,
      indexOfFirstPrimitiveInGeometry,
      indexOfLastPrimitivesInGeometry
    ) {
      let indexOfFirstSat = 0
      let indexOfLastSat = 0
      for (
        let planeIndex = 0;
        planeIndex < indexOfFirstPrimitiveInGeometry;
        planeIndex++
      ) {
        indexOfFirstSat += nbOfSatsPerPlane[planeIndex]
      }
      indexOfLastSat += indexOfFirstSat
      for (
        let planeIndex = indexOfFirstPrimitiveInGeometry;
        planeIndex < indexOfLastPrimitivesInGeometry;
        planeIndex++
      ) {
        indexOfLastSat += nbOfSatsPerPlane[planeIndex]
      }
      return [indexOfFirstSat, indexOfLastSat]
    },

    togglePlanes(primitives, startIndex, stopIndex) {
      for (let i = startIndex; i < stopIndex; i++) {
        primitives.get(i).show = !primitives.get(i).show
      }
    },

    toggleSatellites(entities, startIndex, stopIndex) {
      for (let satIndex = startIndex; satIndex < stopIndex; satIndex++) {
        entities[satIndex].show = !entities[satIndex].show
      }
    },

    updateGroundStations(index) {
      let constellationToUseForGroundStationsVisibility = null
      if (this.isAnalyticalDashboard) {
        constellationToUseForGroundStationsVisibility =
          this.$parent.currentResultsLog.results
            .homogeneousConstellationGeometries[index]
      } else {
        constellationToUseForGroundStationsVisibility =
          this.visibilityData[index].constellation
      }
      this.$parent.addGroundStations(
        [constellationToUseForGroundStationsVisibility],
        this.$parent.currentResultsLog.results.groundStations
      )
      this.constellationsUIElements.forEach((el) => {
        el.groundStationIconColor = 'white'
      })
      this.constellationsUIElements[index].groundStationIconColor = 'green'
    }
  },

  watch: {
    constellations: {
      handler() {
        this.updateConstellationsUIElements()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.dot-satellite {
  height: 10px;
  width: 10px;
  background-color: #00a6ff;
  border-radius: 50%;
  display: inline-block;
}

.legendText {
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  color: white !important;
}
.clickable {
  cursor: pointer;
}
.ruled {
  text-decoration: line-through;
}

#satList-icon.fa-icon {
  margin-top: 6px;
}

#satList-button:hover ~ #toolbar-satellites-list,
#satList-icon:hover ~ #toolbar-satellites-list,
#toolbar-satellites-list:hover {
  visibility: visible !important;
  opacity: 1 !important;
  transform: translate(0, 0) !important;
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;
  z-index: 4;
}

#cesium-viewer-bottom {
  right: auto !important;
  left: 0 !important;
}

#toolbar-satellites-list {
  position: absolute;
  top: auto;
  right: 0;
  height: fit-content;
  width: 320px;
  max-height: 500px;
  margin-top: 5px;
  padding: 6px;
  display: block;
  display: grid;
  border: 1px solid #444;
  background-color: rgba(38, 38, 38, 0.75);
  box-sizing: content-box;
  grid-template-columns: 100%;
  grid-row-gap: 1em;
  overflow-y: auto;
  overflow-x: none;
  border-radius: 10px;
  user-select: none;
  transform: translate(0, -20%);
  visibility: hidden;
  opacity: 0;
  transition: visibility 0s 0.2s, opacity 0.2s ease-in, transform 0.2s ease-in;
  z-index: 4;
  align-content: space-between;
  scrollbar-width: thin;
  scrollbar-color: #444 rgba(255, 255, 255, 0.75);
}

#toolbar-satellites-list::-webkit-scrollbar {
  width: 8px;
}

#toolbar-satellites-list::-webkit-scrollbar-track {
  background-color: rgba(255, 255, 255, 0.75);
  border-radius: 100px;
}

#toolbar-satellites-list::-webkit-scrollbar-thumb {
  background-color: #444;
  border-radius: 100px;
}

.satellite-row {
  display: grid;
  grid-template-columns: 60% 20% 20%;
  border-bottom: 1px dashed white;
  height: 100%;
  align-content: space-between;
}

.satellite-name-col {
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.satellite-icon-col {
  text-align: center;
  margin: auto;
}
</style>
